import { forwardRef, useEffect, useMemo, useState } from 'react';
import type {
  DraggableSyntheticListeners,
  UniqueIdentifier,
} from '@dnd-kit/core';
import type { Transform } from '@dnd-kit/utilities';
import type { TWidget } from 'types/hubDashboard.type';
import { ConfirmationPopup, Icon } from '@timeedit/ui-components';
import HubWidgetWrapper from '../../Dashboard/HubWidgetWrapper';
import './SortableItem.scss';

export interface ItemProps {
  dragOverlay?: boolean;
  dragging?: boolean;
  activatorRef?: (element: HTMLElement | null) => void;
  index?: number;
  id: UniqueIdentifier;
  widget: TWidget;
  transform?: Transform | null;
  draggable?: boolean;
  removable?: boolean;
  listeners?: DraggableSyntheticListeners;
  transition?: string | null;
  onRemove?: ({
    widgetId,
    index,
  }: {
    widgetId: UniqueIdentifier;
    index: number;
  }) => void;
  onEdit?: ({
    widgetId,
    index,
  }: {
    widgetId: UniqueIdentifier;
    index: number;
  }) => void;
}

const SortableItem = forwardRef<HTMLDivElement, ItemProps>(
  (
    {
      dragOverlay,
      dragging,
      activatorRef,
      index,
      listeners,
      widget,
      id,
      onRemove,
      onEdit,
      transition,
      transform,
      draggable = true,
      removable = true,
      ...props
    },
    ref
  ) => {
    useEffect(() => {
      if (!dragOverlay) {
        return;
      }

      document.body.classList.add('hub:cursor-grabbing');

      return () => {
        document.body.classList.remove('hub:cursor-grabbing');
      };
    }, [dragOverlay]);

    const transformStyle = useMemo(() => {
      if (!transform) return '';

      let transformCSS = '';
      transformCSS += transform.x
        ? `translateX(${Math.round(transform.x)}px) `
        : '';
      transformCSS += transform.y
        ? `translateY(${Math.round(transform.y)}px) `
        : '';
      transformCSS += transform.scaleX
        ? `scaleX(${Math.round(transform.scaleX)}) `
        : '';
      transformCSS += transform.scaleY
        ? `scaleY(${Math.round(transform.scaleY)}) `
        : '';

      return transformCSS;
    }, [transform]);

    return (
      <div
        style={
          {
            transition,
            transform: transformStyle,
            zIndex: index,
          } as React.CSSProperties
        }
        ref={ref}
      >
        <div
          className={`
            sortable-item 
            ${dragging ? 'sortable-item-moving' : ''}
            ${dragOverlay ? `animate-pop` : ``}
          `}
          {...props}
        >
          <HubWidgetWrapper
            widget={widget}
            actions={[
              <span
                key="edit"
                onClick={() => onEdit({ widgetId: id, index })}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    event.stopPropagation();
                    onEdit({ widgetId: id, index });
                  }
                }}
                tabIndex={0}
                role="button"
              >
                <Icon id="EditOutlined" alt="Edit" />
              </span>,
              <ConfirmationPopup
                key="confirm-remove"
                message="Are you sure you want to remove this widget?"
                onConfirm={() => onRemove({ widgetId: id, index })}
              >
                <span tabIndex={0} role="button">
                  <Icon id="CloseOutlined" alt="Close" />
                </span>
              </ConfirmationPopup>,
              <span
                key="drag"
                ref={activatorRef}
                {...listeners}
                tabIndex={0}
                role="button"
              >
                <Icon id="DragOutlined" alt="Drag" />
              </span>,
            ]}
          />
        </div>
      </div>
    );
  }
);

SortableItem.displayName = 'SortableItem';

export default SortableItem;
